import type { SupabaseClient } from "@supabase/supabase-js";

// Schedule S3: lineage read, before-start bounds revision, and atomic same-seat replacement.
// Host RPCs return bounded JSON envelopes; member labels are redacted and no guard user UUID
// ever crosses this boundary.

export const SCHEDULE_WRITE_UNAVAILABLE =
  "Schedule changes are unavailable right now. Check your access or try again.";

export const SAFE_LINEAGE_READ_FAILURE = "Couldn't load this slot's lineage. Please try again.";
export const SAFE_REVISE_BOUNDS_FAILURE = "Couldn't revise this slot. Please try again.";
export const SAFE_REPLACE_FAILURE = "Couldn't replace that assignment. Please try again.";

export const SCHEDULE_LINEAGE_SOURCE_INCOMPLETE =
  "This slot's service seats are incomplete, so replace and revise are unavailable. Publish a new slot after capture started, or open Schedule, Timesheets.";

export const SCHEDULE_LINEAGE_TOO_LARGE =
  "This slot has too many seats to load here. Narrow the headcount or open Schedule, Timesheets.";

export const SCHEDULE_WEEK_ENDED =
  "This week has ended. Review it in Schedule, Timesheets.";

export const SCHEDULE_SEAT_STARTED =
  "This seat has started. Time and headcount cannot change now; cancel or replace instead.";

export const SCHEDULE_SLOT_REVISION_CHANGED =
  "This slot changed while you were editing it. Refresh the schedule and try again.";

export const SCHEDULE_APPROVED_WEEK_EDIT_NOTE =
  "This week was approved. Editing it will need a new approval.";

const LINEAGE_TAGS = [
  "declined_before_duty",
  "assigned",
  "replaced_before_service",
  "handoff_during_service",
] as const;

export type LineageDutyTag = (typeof LINEAGE_TAGS)[number];

const LINEAGE_TAG_SET: ReadonlySet<string> = new Set(LINEAGE_TAGS);

const FILL_STATES = ["assigned", "open", "cancelled"] as const;
export type SeatFillState = (typeof FILL_STATES)[number];

const FILL_STATE_SET: ReadonlySet<string> = new Set(FILL_STATES);

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID.test(value);
}

function isInstant(value: unknown): value is string {
  if (typeof value !== "string" || value.length === 0) {
    return false;
  }
  return !Number.isNaN(new Date(value).getTime());
}

function isSeatOrdinal(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 1 && value <= 100;
}

function isHeadcount(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 1 && value <= 100;
}

function readDutyIntervals(value: unknown): readonly ScheduleDutyInterval[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const intervals: ScheduleDutyInterval[] = [];
  for (const entry of value) {
    if (entry === null || typeof entry !== "object") {
      continue;
    }
    const row = entry as Record<string, unknown>;
    if (
      typeof row.personLabel !== "string" ||
      !isInstant(row.start) ||
      !isInstant(row.end) ||
      typeof row.lineageTag !== "string" ||
      !LINEAGE_TAG_SET.has(row.lineageTag)
    ) {
      continue;
    }
    intervals.push({
      personLabel: row.personLabel,
      start: row.start,
      end: row.end,
      lineageTag: row.lineageTag as LineageDutyTag,
    });
  }
  return intervals;
}

function readSeat(value: unknown): ScheduleLineageSeat | null {
  if (value === null || typeof value !== "object") {
    return null;
  }
  const row = value as Record<string, unknown>;
  if (
    !isSeatOrdinal(row.seatOrdinal) ||
    typeof row.seatLocator !== "string" ||
    row.seatLocator.length === 0 ||
    typeof row.fillState !== "string" ||
    !FILL_STATE_SET.has(row.fillState) ||
    !isInstant(row.scheduledStart) ||
    !isInstant(row.scheduledEnd) ||
    !isUuid(row.expectedRevision) ||
    typeof row.hasOutgoingAssignment !== "boolean"
  ) {
    return null;
  }
  const memberLabel =
    row.memberLabel === null
      ? null
      : typeof row.memberLabel === "string" && row.memberLabel.length > 0
        ? row.memberLabel
        : null;
  const outgoingAssignmentRef =
    row.outgoingAssignmentRef === null
      ? null
      : typeof row.outgoingAssignmentRef === "string" && row.outgoingAssignmentRef.length > 0
        ? row.outgoingAssignmentRef
        : null;
  return {
    seatOrdinal: row.seatOrdinal,
    seatLocator: row.seatLocator,
    fillState: row.fillState as SeatFillState,
    scheduledStart: row.scheduledStart,
    scheduledEnd: row.scheduledEnd,
    expectedRevision: row.expectedRevision,
    memberLabel,
    hasOutgoingAssignment: row.hasOutgoingAssignment,
    outgoingAssignmentRef,
    dutyIntervals: readDutyIntervals(row.dutyIntervals),
  };
}

export interface ScheduleDutyInterval {
  readonly personLabel: string;
  readonly start: string;
  readonly end: string;
  readonly lineageTag: LineageDutyTag;
}

export interface ScheduleLineageSeat {
  readonly seatOrdinal: number;
  readonly seatLocator: string;
  readonly fillState: SeatFillState;
  readonly scheduledStart: string;
  readonly scheduledEnd: string;
  readonly expectedRevision: string;
  readonly memberLabel: string | null;
  readonly hasOutgoingAssignment: boolean;
  readonly outgoingAssignmentRef: string | null;
  readonly dutyIntervals: readonly ScheduleDutyInterval[];
}

export interface ScheduleSlotLineage {
  readonly weekEnded: boolean;
  readonly canReviseBounds: boolean;
  readonly seats: readonly ScheduleLineageSeat[];
  /** Opaque slot revision shared by every seat in the response. */
  readonly expectedRevision: string;
}

export type ScheduleSlotLineageOutcome =
  | { ok: true; lineage: ScheduleSlotLineage }
  | { ok: false; kind: "unavailable"; reason: string };

const LINEAGE_UNAVAILABLE_COPY: Readonly<Record<string, string>> = {
  target_unavailable: SCHEDULE_WRITE_UNAVAILABLE,
  source_incomplete: SCHEDULE_LINEAGE_SOURCE_INCOMPLETE,
  too_large: SCHEDULE_LINEAGE_TOO_LARGE,
  unavailable: SCHEDULE_WRITE_UNAVAILABLE,
};

/** Map a host unavailable lineage envelope to operator copy. Null when not that shape. */
export function readLineageUnavailable(value: unknown): ScheduleSlotLineageOutcome | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const row = value as Record<string, unknown>;
  if (row.status !== "unavailable" || typeof row.reason !== "string") {
    return null;
  }
  return {
    ok: false,
    kind: "unavailable",
    reason: LINEAGE_UNAVAILABLE_COPY[row.reason] ?? SAFE_LINEAGE_READ_FAILURE,
  };
}

export function readLineageOk(value: unknown): ScheduleSlotLineage | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const row = value as Record<string, unknown>;
  if (row.status !== "ok" || typeof row.weekEnded !== "boolean" || typeof row.canReviseBounds !== "boolean") {
    return null;
  }
  if (!Array.isArray(row.seats) || row.seats.length === 0 || row.seats.length > 100) {
    return null;
  }
  const seats: ScheduleLineageSeat[] = [];
  for (const entry of row.seats) {
    const seat = readSeat(entry);
    if (seat === null) {
      return null;
    }
    seats.push(seat);
  }
  const revision = seats[0]!.expectedRevision;
  if (!seats.every((seat) => seat.expectedRevision === revision)) {
    return null;
  }
  return {
    weekEnded: row.weekEnded,
    canReviseBounds: row.canReviseBounds,
    seats,
    expectedRevision: revision,
  };
}

const REFUSED_REASON_COPY: Readonly<Record<string, string>> = {
  week_ended: SCHEDULE_WEEK_ENDED,
  seat_started: SCHEDULE_SEAT_STARTED,
  seat_ended: "This shift has ended.",
  invalid_bounds: "Enter a valid start time, end time, and headcount.",
  same_officer: "Choose a different member than the one currently assigned.",
  officer_double_booked:
    "Member already has an overlapping scheduled or active route assignment.",
  seat_unavailable: "That seat is not available right now.",
  seat_cancelled: "This seat was cancelled after publish.",
  assignment_unavailable: "No assignment is available to replace on this seat.",
  worked_assignment: "Completed work cannot be replaced from here.",
};

export function refusedReasonToCopy(reason: unknown): string | null {
  return typeof reason === "string" && reason in REFUSED_REASON_COPY
    ? REFUSED_REASON_COPY[reason]!
    : null;
}

export type ReviseBoundsOutcome =
  | { ok: true; outcome: "revised"; expectedRevision: string }
  | { ok: false; kind: "conflict" | "refused" | "unavailable"; reason: string };

export type ReplaceSeatOutcome =
  | {
      ok: true;
      outcome: "replaced" | "handoff_recorded";
      seatOrdinal: number;
      outgoingAssignmentRef: string;
      expectedRevision: string;
    }
  | { ok: false; kind: "conflict" | "refused" | "unavailable"; reason: string };

function readWriteEnvelope(
  value: unknown,
  safeFailure: string,
): { ok: false; kind: "conflict" | "refused" | "unavailable"; reason: string } | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return { ok: false, kind: "unavailable", reason: safeFailure };
  }
  const row = value as Record<string, unknown>;
  if (row.status === "unavailable") {
    return { ok: false, kind: "unavailable", reason: SCHEDULE_WRITE_UNAVAILABLE };
  }
  if (row.status === "conflict" && row.reason === "revision_changed") {
    return { ok: false, kind: "conflict", reason: SCHEDULE_SLOT_REVISION_CHANGED };
  }
  if (row.status === "refused") {
    const copy = refusedReasonToCopy(row.reason);
    return {
      ok: false,
      kind: "refused",
      reason: copy ?? safeFailure,
    };
  }
  return null;
}

/** Complete bounded read model for one published or cancelled slot. Never throws. */
export async function fetchScheduleSlotLineage(
  client: SupabaseClient,
  accountId: string,
  shiftSlotId: string,
): Promise<ScheduleSlotLineageOutcome> {
  try {
    const { data, error } = await client.rpc("management_schedule_slot_lineage", {
      p_account_id: accountId,
      p_shift_slot_id: shiftSlotId,
    });
    if (error !== null) {
      return { ok: false, kind: "unavailable", reason: SAFE_LINEAGE_READ_FAILURE };
    }
    const unavailable = readLineageUnavailable(data);
    if (unavailable !== null) {
      return unavailable;
    }
    const lineage = readLineageOk(data);
    return lineage === null
      ? { ok: false, kind: "unavailable", reason: SAFE_LINEAGE_READ_FAILURE }
      : { ok: true, lineage };
  } catch {
    return { ok: false, kind: "unavailable", reason: SAFE_LINEAGE_READ_FAILURE };
  }
}

/** Before-start slot bounds and headcount writer. Never throws. */
export async function reviseScheduleSlotBounds(
  client: SupabaseClient,
  accountId: string,
  shiftSlotId: string,
  expectedRevision: string,
  startsAt: string,
  endsAt: string,
  headcountRequired: number,
): Promise<ReviseBoundsOutcome> {
  try {
    const { data, error } = await client.rpc("revise_schedule_slot_bounds", {
      p_account_id: accountId,
      p_shift_slot_id: shiftSlotId,
      p_expected_revision: expectedRevision,
      p_starts_at: startsAt,
      p_ends_at: endsAt,
      p_headcount_required: headcountRequired,
    });
    if (error !== null) {
      return { ok: false, kind: "unavailable", reason: SAFE_REVISE_BOUNDS_FAILURE };
    }
    const refused = readWriteEnvelope(data, SAFE_REVISE_BOUNDS_FAILURE);
    if (refused !== null) {
      return refused;
    }
    if (data === null || typeof data !== "object" || Array.isArray(data)) {
      return { ok: false, kind: "unavailable", reason: SAFE_REVISE_BOUNDS_FAILURE };
    }
    const row = data as Record<string, unknown>;
    if (row.status !== "ok" || row.outcome !== "revised" || !isUuid(row.expectedRevision)) {
      return { ok: false, kind: "unavailable", reason: SAFE_REVISE_BOUNDS_FAILURE };
    }
    return { ok: true, outcome: "revised", expectedRevision: row.expectedRevision };
  } catch {
    return { ok: false, kind: "unavailable", reason: SAFE_REVISE_BOUNDS_FAILURE };
  }
}

/** Atomic same-seat replacement writer. Never throws. */
export async function replaceScheduleSeatAssignment(
  client: SupabaseClient,
  accountId: string,
  shiftSlotId: string,
  seatOrdinal: number,
  expectedRevision: string,
  incomingMembershipId: string,
): Promise<ReplaceSeatOutcome> {
  try {
    const { data, error } = await client.rpc("replace_schedule_seat_assignment", {
      p_account_id: accountId,
      p_shift_slot_id: shiftSlotId,
      p_seat_ordinal: seatOrdinal,
      p_expected_revision: expectedRevision,
      p_incoming_membership_id: incomingMembershipId,
    });
    if (error !== null) {
      return { ok: false, kind: "unavailable", reason: SAFE_REPLACE_FAILURE };
    }
    const refused = readWriteEnvelope(data, SAFE_REPLACE_FAILURE);
    if (refused !== null) {
      return refused;
    }
    if (data === null || typeof data !== "object" || Array.isArray(data)) {
      return { ok: false, kind: "unavailable", reason: SAFE_REPLACE_FAILURE };
    }
    const row = data as Record<string, unknown>;
    if (
      row.status !== "ok" ||
      (row.outcome !== "replaced" && row.outcome !== "handoff_recorded") ||
      !isSeatOrdinal(row.seatOrdinal) ||
      typeof row.outgoingAssignmentRef !== "string" ||
      row.outgoingAssignmentRef.length === 0 ||
      !isUuid(row.expectedRevision)
    ) {
      return { ok: false, kind: "unavailable", reason: SAFE_REPLACE_FAILURE };
    }
    return {
      ok: true,
      outcome: row.outcome,
      seatOrdinal: row.seatOrdinal,
      outgoingAssignmentRef: row.outgoingAssignmentRef,
      expectedRevision: row.expectedRevision,
    };
  } catch {
    return { ok: false, kind: "unavailable", reason: SAFE_REPLACE_FAILURE };
  }
}

export { isUuid, isHeadcount, readSeat };
